<template>
  <div class="mb-5">
    <div
      class="col-12 d-flex flex-wrap filter-row"
    >
      <div>
        <base-input :label="$t('COMMON.PER_PAGE')">
          <el-select
            class="select-primary pagination-select"
            v-model="pagination.perPage"
            :placeholder="$t('COMMON.PER_PAGE')"
          >
            <el-option
              class="select-primary"
              v-for="item in pagination.perPageOptions"
              :key="item"
              :label="item"
              :value="item"
            >
            </el-option>
          </el-select>
        </base-input>
      </div>
      <div>
        <base-input :label="$t('COMMON.CATEGORIES')">
          <category-group-selector
            :allowNone="false"
            :showAll="true"
            :isOnlyModels="false"
            :placeholder="$t('COMMON.CATEGORIES')"
            @categoryChanged="
              (categoryId) => {
                selectedCategory = categoryId;
              }
            "
          />
        </base-input>
      </div>
      <div>
        <base-input
          v-model="query"
          type="search"
          prepend-icon="fas fa-search"
          :placeholder="$t('COMMON.SEARCH_HINT.PRODUCT_WITH_SUPPLIER_CODE')"
          :label="$t('COMMON.SEARCH')"
          clearable
        />
      </div>
      <div v-if="purchasesOrder.status == ORDER_STATUS_DRAFT">
        <base-checkbox v-model="displayZeroQuantities">
          <span class="form-control-label">
            {{ $t("COMMON.DISPLAY_ZERO_LINES") }}
          </span>
        </base-checkbox>
      </div>
    </div>

    <el-table
      class="table-responsive align-items-center table-flush"
      header-row-class-name="thead-light"
      ref="purchasesOrderItemsTable"
      :row-class-name="getProductRowClassName"
      :data="filteredProducts.slice(startIndex, endIndex)"
    >
      <div slot="empty" v-if="loading">
        <img src="/img/loading.gif" style="height: 100px; width: 100px" />
      </div>
      <el-table-column :label="$t('COMMON.CODE')" prop="code" min-width="200">
        <template v-slot="{ row }">
          <div v-if="row">
            <div style="display: flex; align-items: center; gap: 8px">
              <a href="#" @click.prevent="goToPurchasesOrderable(row)">
                {{ row.name }}
              </a>
              <i
                v-if="row.status === STATUS_BACKORDER"
                class="fas fa-exclamation-triangle text-warning"
                style="cursor: pointer; font-size: 16px"
                :title="$t('PRODUCTS.PRODUCT_IN_BACKORDER')"
                @click="showBackorderModal(row)"
              ></i>
              <i
                v-if="row.status === STATUS_DELETED"
                class="fas fa-ban text-danger"
                style="cursor: pointer; font-size: 16px"
                :title="$t('PRODUCTS.PRODUCT_DELETED')"
                @click="showDeletedProductModal(row)"
              ></i>
              <span
                v-if="hasHalfUnitSurcharge(row)"
                class="badge badge-info half-unit-badge"
                :title="$t('PRODUCTS.CAN_HAVE_HALF_UNIT_HELPER')"
              >
                ½
              </span>
              <el-tooltip
                v-if="row.special_order"
                :content="$t('PRODUCTS.SPECIAL_ORDER_HELPER')"
                placement="top"
              >
                <span class="badge badge-warning special-order-badge">
                  <i class="fas fa-asterisk mr-1"></i>
                  {{ $t("PRODUCTS.SPECIAL_ORDER") }}
                </span>
              </el-tooltip>
            </div>
            <br />
            <div class="text-muted purchasesOrder-item-excerpt">
              <ul>
                <li>
                  {{ $t("PRODUCTS.CODE") }} :
                  {{ row.code }}
                </li>
                <li>
                  {{ $t("PRODUCTS.SKU") }} :
                  {{ row.sku }}
                </li>
                <li>
                  {{ $t("PRODUCTS.SUPPLIER_CODE") }} :
                  {{ row.supplierProduct.supplier_code }}
                </li>
                <li>
                  {{ $t("PRODUCTS.SUPPLIER_SKU") }} :
                  {{ row.supplierProduct.sku }}
                </li>
              </ul>
            </div>
          </div>
        </template>
      </el-table-column>

      <el-table-column
        :label="$t('COMMON.QUANTITY')"
        class-name="number-column"
        label-class-name="number-column"
      >
        <template v-slot="{ row }">
          <base-input
            :disabled="purchasesOrder.status != ORDER_STATUS_DRAFT"
            v-model="itemsQuantities[row.id]"
            type="number"
            :step="hasHalfUnitSurcharge(row) ? 0.1 : 1"
            placeholder="0"
            @change="
              (event) => {
                updatePurchasesOrderItem(row, event.target.value);
              }
            "
          />
        </template>
      </el-table-column>

      <el-table-column
        :label="$t('COMMON.FORMAT')"
        class-name="number-column"
        label-class-name="number-column"
      >
        <template v-slot="{ row }">
          <object-link :object="row.orderUnitOfMeasureUnit" />
        </template>
      </el-table-column>

      <el-table-column
        :label="$t('COMMON.UNIT_PRICE')"
        prop="orderUnitOfMeasureUnitPrice"
        class-name="number-column"
        label-class-name="number-column"
      >
        <template v-slot="{ row }">
          {{ $formatCurrency(row.orderUnitOfMeasureUnitPrice) }}
        </template>
      </el-table-column>

      <el-table-column
        :label="$t('COMMON.SUBTOTAL')"
        class-name="number-column"
        label-class-name="number-column"
      >
        <template v-slot="{ row }">
          {{
            $formatCurrency(
              itemsByProduct[row.id]
                ? itemsByProduct[row.id].pricing.subtotal
                : 0
            )
          }}
        </template>
      </el-table-column>

      <el-table-column
        :label="$t('COMMON.TAXES')"
        class-name="text-center"
        label-class-name="text-center"
      >
        <template v-slot="{ row }">
          <tax-groups-selector
            v-if="canEditTaxGroups && itemsByProduct[row.id]"
            :filterOrganization="purchasesOrder.organization.id"
            :taxGroups="getItemTaxGroups(row)"
            @taxGroupsChanged="
              (taxGroups) => updatePurchasesOrderItemTaxGroups(row, taxGroups)
            "
          />
          <template v-else>
            <span
              v-for="tax in getItemTaxLabels(row)"
              :key="tax"
              class="badge badge-secondary mr-1"
            >
              {{ tax }}
            </span>
          </template>
        </template>
      </el-table-column>
    </el-table>
    <div
      slot="footer"
      class="mt-5 col-12 d-flex justify-content-center justify-content-sm-between flex-wrap table-footer"
    >
      <div class="">
        <p class="card-category">
          {{
            $t("COMMON.DISPLAY_FROM_X_TO_X_OF_X_ENTRIES", {
              from: total ? from + 1 : 0,
              to: to,
              of: total,
            })
          }}
        </p>
      </div>
      <base-pagination
        class="pagination-no-border"
        v-model="pagination.currentPage"
        :per-page="pagination.perPage"
        :total="total"
      />
    </div>

    <!-- Backorder Modal -->
    <modal
      :show.sync="backorderModalOpened"
      modal-classes="modal-secondary"
      size="lg"
    >
      <div>
        <div class="d-flex align-items-center mb-3">
          <i
            class="fas fa-exclamation-triangle text-warning mr-2"
            style="font-size: 24px"
          ></i>
          <h2 class="mb-0">
            {{ $t("PRODUCTS.PRODUCT_IN_BACKORDER") }}
          </h2>
        </div>

        <div v-if="selectedBackorderProduct">
          <div class="mb-3">
            <strong>{{ $t("COMMON.PRODUCT") }}:</strong>
            {{ selectedBackorderProduct.name }}
          </div>

          <div
            class="mb-3"
            v-if="selectedBackorderProduct.return_to_stock_date"
          >
            <strong>{{ $t("PRODUCTS.RETURN_TO_STOCK_DATE") }}:</strong>
            {{
              $timeZoneDateFormat(
                selectedBackorderProduct.return_to_stock_date,
                "LL"
              )
            }}
            <br />
            <small class="text-muted">
              {{
                $t("PRODUCTS.PRODUCT_BACK_IN_STOCK_ON_X", {
                  date: $timeZoneDateFormat(
                    selectedBackorderProduct.return_to_stock_date,
                    "LL"
                  ),
                })
              }}
            </small>
          </div>

          <div
            v-if="
              selectedBackorderProduct.replacements &&
              selectedBackorderProduct.replacements.length > 0
            "
          >
            <strong>{{ $t("PRODUCTS.REPLACEMENTS") }}:</strong>
            <div class="mt-2">
              <div
                v-for="replacement in selectedBackorderProduct.replacements"
                :key="replacement.id"
                class="replacement-line d-flex align-items-center justify-content-between mb-2"
              >
                <div class="d-flex flex-column">
                  <object-link :object="replacement" />
                  <div class="replacement-meta">
                    {{ $t("PRODUCTS.SKU") }} :
                    <span class="text-body">
                      {{ replacement.sku || "-" }}
                    </span>
                  </div>
                </div>
                <div class="replacement-actions d-flex align-items-center">
                  <el-tooltip :content="$t('COMMON.COPY')" placement="top">
                    <i
                      class="fa fa-copy action-icon mr-3"
                      @click="copyReplacementCode(replacement.sku)"
                    ></i>
                  </el-tooltip>
                  <a
                    href="#"
                    class="replacement-link d-flex align-items-center"
                    @click.prevent="scrollToReplacement(replacement.id)"
                  >
                    <i class="fa fa-link mr-1"></i>
                    {{ $t("PRODUCTS.GO_TO_REPLACEMENT_LINE") }}
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div class="mt-3" v-if="selectedBackorderProduct.backorder_note">
            <strong>{{ $t("PRODUCTS.BACKORDER_NOTE") }}:</strong>
            <div class="mt-1">
              {{ selectedBackorderProduct.backorder_note }}
            </div>
          </div>

          <div class="alert alert-warning mt-3">
            <i class="fas fa-info-circle mr-2"></i>
            {{ $t("PRODUCTS.BACKORDER_QUANTITY_CHANGE_BLOCKED") }}
          </div>
        </div>

        <div class="mt-4 text-right">
          <base-button type="secondary" @click="backorderModalOpened = false">
            {{ $t("COMMON.CLOSE") }}
          </base-button>
        </div>
      </div>
    </modal>

    <modal
      :show.sync="deletedProductModalOpened"
      modal-classes="modal-secondary"
      size="lg"
    >
      <div>
        <div class="d-flex align-items-center mb-3">
          <i class="fas fa-ban text-danger mr-2" style="font-size: 24px"></i>
          <h2 class="mb-0">
            {{ $t("PRODUCTS.PRODUCT_DELETED") }}
          </h2>
        </div>

        <div v-if="selectedDeletedProduct">
          <div class="mb-3">
            <strong>{{ $t("COMMON.PRODUCT") }}:</strong>
            {{ selectedDeletedProduct.name }}
          </div>

          <p class="text-muted">
            {{ $t("PRODUCTS.DELETED_PRODUCT_REPLACEMENTS_MESSAGE") }}
          </p>

          <div
            v-if="
              selectedDeletedProduct.replacements &&
              selectedDeletedProduct.replacements.length > 0
            "
          >
            <strong>{{ $t("PRODUCTS.REPLACEMENTS") }}:</strong>
            <div class="mt-2">
              <div
                v-for="replacement in selectedDeletedProduct.replacements"
                :key="replacement.id"
                class="replacement-line d-flex align-items-center justify-content-between mb-2"
              >
                <div class="d-flex flex-column">
                  <object-link :object="replacement" />
                  <div class="replacement-meta">
                    {{ $t("PRODUCTS.SKU") }} :
                    <span class="text-body">
                      {{ replacement.sku || "-" }}
                    </span>
                  </div>
                </div>
                <div class="replacement-actions d-flex align-items-center">
                  <el-tooltip :content="$t('COMMON.COPY')" placement="top">
                    <i
                      class="fa fa-copy action-icon mr-3"
                      @click="copyReplacementCode(replacement.sku)"
                    ></i>
                  </el-tooltip>
                  <a
                    href="#"
                    class="replacement-link d-flex align-items-center"
                    @click.prevent="scrollToReplacement(replacement.id)"
                  >
                    <i class="fa fa-link mr-1"></i>
                    {{ $t("PRODUCTS.GO_TO_REPLACEMENT_LINE") }}
                  </a>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="alert alert-warning mt-3">
            <i class="fas fa-info-circle mr-2"></i>
            {{ $t("PRODUCTS.NO_REPLACEMENTS_AVAILABLE") }}
          </div>

          <div class="mt-3" v-if="selectedDeletedProduct.backorder_note">
            <strong>{{ $t("PRODUCTS.BACKORDER_NOTE") }}:</strong>
            <div class="mt-1">{{ selectedDeletedProduct.backorder_note }}</div>
          </div>
        </div>

        <div class="alert alert-warning mt-3">
          <i class="fas fa-info-circle mr-2"></i>
          {{ $t("PRODUCTS.DELETED_PRODUCT_QUANTITY_BLOCKED") }}
        </div>

        <div class="mt-4 text-right">
          <base-button
            type="secondary"
            @click="deletedProductModalOpened = false"
          >
            {{ $t("COMMON.CLOSE") }}
          </base-button>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import {
  Table,
  TableColumn,
  DropdownMenu,
  DropdownItem,
  Dropdown,
  Tooltip,
  Select,
  Option,
  Button,
} from "element-ui";
import formMixin from "@/mixins/form-mixin";
import taxDisplayMixin from "@/mixins/tax-display-mixin";
import { ORDER_STATUS_DRAFT } from "@/constants/orders";
import { STATUS_BACKORDER, STATUS_DELETED } from "@/constants/products";
import { BaseInput } from "@/components";
import { cloneDeep } from "lodash";
import defaultPurchasesOrderItem from "../defaultPurchasesOrderItem";
import CategoryGroupSelector from "@/components/CategoryGroupSelector.vue";
import TaxGroupsSelector from "@/components/TaxGroupsSelector.vue";
import swal from "sweetalert2";

export default {
  name: "purchases-order-view-details-purchases-order-item-table-v2",

  components: {
    BaseInput,
    CategoryGroupSelector,
    TaxGroupsSelector,
    [Tooltip.name]: Tooltip,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Dropdown.name]: Dropdown,
    [DropdownItem.name]: DropdownItem,
    [DropdownMenu.name]: DropdownMenu,
    [Select.name]: Select,
    [Option.name]: Option,
    [Button.name]: Button,
  },

  mixins: [formMixin, taxDisplayMixin],

  props: ["purchasesOrder"],

  data() {
    const itemsQuantities = {};
    this.purchasesOrder.items.forEach((item) => {
      itemsQuantities[item.purchasesOrderable.id] = item.quantity;
    });

    const itemsByProduct = {};
    this.purchasesOrder.items.forEach((item) => {
      itemsByProduct[item.purchasesOrderable.id] = item;
    });

    return {
      pagination: {
        perPage: 500,
        currentPage: 1,
        perPageOptions: [20, 50, 100, 500],
      },
      loading: false,
      ORDER_STATUS_DRAFT: ORDER_STATUS_DRAFT,
      STATUS_BACKORDER: STATUS_BACKORDER,
      STATUS_DELETED: STATUS_DELETED,
      products: [],
      filteredProducts: [],
      itemsQuantities: itemsQuantities,
      itemsByProduct: itemsByProduct,
      query: "",
      displayZeroQuantities: this.purchasesOrder.status == ORDER_STATUS_DRAFT,
      selectedCategory: null,
      backorderModalOpened: false,
      selectedBackorderProduct: null,
      deletedProductModalOpened: false,
      selectedDeletedProduct: null,
      lastProductsFetchSignature: null,
      productsSignatureLoaded: null,
      activeProductsRequestToken: null,
    };
  },

  created() {
    this.getProducts();
  },

  computed: {
    from() {
      return this.pagination.perPage * (this.pagination.currentPage - 1);
    },

    to() {
      let highBound = this.from + this.pagination.perPage;
      if (this.total < highBound) {
        highBound = this.total;
      }
      return highBound;
    },

    total() {
      return this.filteredProducts.length;
    },

    canEditTaxGroups() {
      return (
        this.purchasesOrder.status === ORDER_STATUS_DRAFT &&
        this.$currentUserCan(this.$permissions.PERM_EDIT_ORDER_ITEM_TAX_GROUPS)
      );
    },

    startIndex() {
      return (this.pagination.currentPage - 1) * this.pagination.perPage;
    },

    endIndex() {
      return this.startIndex + this.pagination.perPage;
    },
  },

  methods: {
    async goToPurchasesOrderable(purchasesOrderable) {
      this.$router.push(this.$objectViewRoute(purchasesOrderable));
    },

    getItemTaxLabels(row) {
      return this.getOrderItemTaxLabels(row, this.itemsByProduct);
    },

    getItemTaxGroups(row) {
      const item = this.itemsByProduct?.[row.id];
      return item?.taxGroups || this.getProductTaxGroups(row);
    },

    showBackorderModal(product) {
      this.selectedBackorderProduct = product;
      this.backorderModalOpened = true;
    },

    showDeletedProductModal(product) {
      this.selectedDeletedProduct = product;
      this.deletedProductModalOpened = true;
    },

    async copyReplacementCode(code) {
      if (!code) {
        return;
      }

      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(code);
        } else {
          const textarea = document.createElement("textarea");
          textarea.value = code;
          textarea.setAttribute("readonly", "");
          textarea.style.position = "absolute";
          textarea.style.left = "-9999px";
          document.body.appendChild(textarea);
          textarea.select();
          document.execCommand("copy");
          document.body.removeChild(textarea);
        }
        this.$notify({
          type: "success",
          message: this.$t("PRODUCTS.REPLACEMENT_CODE_COPIED"),
        });
      } catch (error) {
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
      }
    },

    scrollToReplacement(productId) {
      if (!productId) {
        return;
      }

      const quantity = this.itemsQuantities[productId] || 0;
      if (!this.displayZeroQuantities && quantity <= 0) {
        this.displayZeroQuantities = true;
        this.$nextTick(() => {
          this.scrollToReplacement(productId);
        });
        return;
      }

      this.$nextTick(() => {
        const productIndex = this.filteredProducts.findIndex(
          (product) => product.id === productId
        );

        if (productIndex === -1) {
          this.$notify({
            type: "warning",
            message: this.$t("PRODUCTS.REPLACEMENT_LINE_NOT_FOUND"),
          });
          return;
        }

        const targetPage =
          Math.floor(productIndex / this.pagination.perPage) + 1;
        if (this.pagination.currentPage !== targetPage) {
          this.pagination.currentPage = targetPage;
          this.$nextTick(() => {
            this.scrollToReplacement(productId);
          });
          return;
        }

        const tableEl =
          this.$refs.purchasesOrderItemsTable &&
          this.$refs.purchasesOrderItemsTable.$el;
        const rowEl =
          tableEl && tableEl.querySelector(`.product-row-${productId}`);

        if (rowEl) {
          rowEl.scrollIntoView({ behavior: "smooth", block: "center" });
          rowEl.classList.add("replacement-target-highlight");
          setTimeout(() => {
            rowEl.classList.remove("replacement-target-highlight");
          }, 10000);
          this.backorderModalOpened = false;
          this.deletedProductModalOpened = false;
        } else {
          this.$notify({
            type: "warning",
            message: this.$t("PRODUCTS.REPLACEMENT_LINE_NOT_FOUND"),
          });
        }
      });
    },

    getProductRowClassName(rowData) {
      const row = rowData && rowData.row ? rowData.row : rowData;
      return row && row.id ? `product-row-${row.id}` : "";
    },

    async getProducts({ force = false } = {}) {
      const signature = this.createProductsFetchSignature();
      if (!force && signature && this.productsSignatureLoaded === signature) {
        this.filterProducts();
        return;
      }

      if (
        !force &&
        this.loading &&
        signature &&
        this.lastProductsFetchSignature === signature
      ) {
        return;
      }

      const requestToken = Symbol("productsRequest");
      this.activeProductsRequestToken = requestToken;

      try {
        this.loading = true;
        this.lastProductsFetchSignature = signature;
        if (!this.purchasesOrder) {
          if (this.activeProductsRequestToken === requestToken) {
            this.loading = false;
            this.activeProductsRequestToken = null;
          }
          return;
        }

        let params = {
          sort: "-created_at",
          filter: {
            organization: this.purchasesOrder.organization?.id,
            can_be_purchased: true,
            supplier: this.purchasesOrder.issuer?.id,
            warehouse: this.purchasesOrder.destinationWarehouse?.id,
          },
          include:
            "taxGroups.taxes,unitOfMeasure,orderUnitOfMeasureUnit,categories,replacements",
          page: {
            number: 1,
            size: 999999,
          },
        };

        await this.$store.dispatch("products/list", params);
        const productsArr = await this.$store.getters["products/list"];

        if (this.activeProductsRequestToken !== requestToken) {
          return;
        }

        this.products = productsArr;
        this.filterProducts();
        this.loading = false;
        this.lastProductsFetchSignature = signature;
        this.productsSignatureLoaded = signature;
        this.activeProductsRequestToken = null;
      } catch (error) {
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
        if (this.activeProductsRequestToken === requestToken) {
          this.loading = false;
          this.lastProductsFetchSignature = null;
          this.productsSignatureLoaded = null;
          this.activeProductsRequestToken = null;
        }
      }
    },

    createProductsFetchSignature() {
      if (!this.purchasesOrder) {
        return null;
      }

      const organizationId = this.purchasesOrder.organization?.id || "";
      const supplierId = this.purchasesOrder.issuer?.id || "";
      const warehouseId = this.purchasesOrder.destinationWarehouse?.id || "";

      return `${organizationId}::${supplierId}::${warehouseId}`;
    },

    syncPurchasesOrderItems() {
      if (!this.purchasesOrder || !this.purchasesOrder.items) {
        return;
      }

      const itemsQuantities = {};
      const itemsByProduct = {};

      this.purchasesOrder.items.forEach((item) => {
        itemsQuantities[item.purchasesOrderable.id] = item.quantity;
        itemsByProduct[item.purchasesOrderable.id] = item;
      });

      this.itemsQuantities = itemsQuantities;
      this.itemsByProduct = itemsByProduct;
      this.filterProducts();
    },

    updatePurchasesOrderItem(product, quantity) {
      if (product.status === this.STATUS_BACKORDER) {
        this.resetProductQuantity(product);
        this.showBackorderModal(product);
        return;
      }

      if (product.status === this.STATUS_DELETED) {
        this.resetProductQuantity(product);
        this.showDeletedProductModal(product);
        return;
      }

      let purchasesOrderItemData = cloneDeep(defaultPurchasesOrderItem);

      const foundItem = this.purchasesOrder.items.find((item) => {
        return item.purchasesOrderable.id == product.id;
      });

      if (foundItem) {
        purchasesOrderItemData = cloneDeep(foundItem);
      }
      if (
        foundItem &&
        (quantity == 0 ||
          isNaN(quantity) ||
          quantity === null ||
          quantity === "")
      ) {
        this.$emit("onDeletePurchasesOrderItem", foundItem.id);
        return;
      }

      purchasesOrderItemData.purchasesOrder.id = this.purchasesOrder.id;
      purchasesOrderItemData.purchasesOrderable = product;
      purchasesOrderItemData.excerpt = product.denomination;

      if (
        product.orderUnitOfMeasureUnit &&
        product.orderUnitOfMeasureUnitPrice
      ) {
        purchasesOrderItemData.unit_price = product.orderUnitOfMeasureUnitPrice;
        purchasesOrderItemData.code = product.sku || product.code;
      } else if (product.warehouseProduct) {
        purchasesOrderItemData.unit_price =
          product.warehouseProduct.buying_price;
        purchasesOrderItemData.code =
          product.warehouseProduct.sku || product.sku || product.code;
      } else if (product.supplierProduct) {
        purchasesOrderItemData.unit_price =
          product.supplierProduct.buying_price;
        purchasesOrderItemData.code =
          product.supplierProduct.sku || product.sku || product.code;
      } else {
        purchasesOrderItemData.unit_price = product.buying_price;
        purchasesOrderItemData.code = product.code;
      }

      if (product.orderUnitOfMeasureUnit) {
        purchasesOrderItemData.unitOfMeasureUnit =
          product.orderUnitOfMeasureUnit;
      }
      if (!this.purchasesOrder.has_no_taxes) {
        purchasesOrderItemData.taxGroups = foundItem
          ? foundItem.taxGroups || []
          : this.getProductTaxGroups(product);
      } else {
        purchasesOrderItemData.taxGroups = [];
      }

      console.log(purchasesOrderItemData);

      if (this.hasHalfUnitSurcharge(product)) {
        const value = parseFloat(quantity);
        const roundedValue = Math.ceil(value * 2) / 2;
        purchasesOrderItemData.quantity = roundedValue.toFixed(2);
        const baseTotal =
          parseFloat(purchasesOrderItemData.unit_price) * roundedValue;
        const isHalfUnit = roundedValue % 1 !== 0;
        const totalWithSurcharge =
          baseTotal +
          parseFloat(
            isHalfUnit ? product.supplierProduct.half_unit_surcharge || 0 : 0
          );
        purchasesOrderItemData.unit_price = totalWithSurcharge / roundedValue;
      } else {
        const value = parseFloat(quantity);
        const roundedValue = Math.ceil(value);
        purchasesOrderItemData.quantity = roundedValue.toFixed(2);
      }

      if (
        isNaN(purchasesOrderItemData.quantity) ||
        purchasesOrderItemData.quantity <= 0
      ) {
        this.itemsQuantities[product.id] = foundItem.quantity;
        swal.fire({
          title: this.$t("COMMON.ERROR"),
          text: this.$t("COMMON.INVALID_QUANTITY"),
          icon: "error",
          confirmButtonText: this.$t("COMMON.OK"),
        });
        return;
      }

      this.$emit("onSubmitPurchasesOrderItemForm", purchasesOrderItemData);
    },

    updatePurchasesOrderItemTaxGroups(product, taxGroups) {
      const foundItem = this.itemsByProduct?.[product.id];
      if (!foundItem) {
        return;
      }

      const purchasesOrderItemData = cloneDeep(foundItem);
      purchasesOrderItemData.purchasesOrder.id = this.purchasesOrder.id;
      purchasesOrderItemData.purchasesOrderable = product;
      purchasesOrderItemData.taxGroups = taxGroups;

      this.$emit("onSubmitPurchasesOrderItemForm", purchasesOrderItemData);
    },

    filterProducts() {
      const query = this.query;
      const augmentedProducts = [...(this.products || [])];
      const productIds = new Set(
        augmentedProducts
          .filter((product) => product && product.id)
          .map((product) => product.id)
      );

      Object.values(this.itemsByProduct || {}).forEach((item) => {
        const purchasesOrderable = item?.purchasesOrderable;

        if (
          !purchasesOrderable ||
          !purchasesOrderable.id ||
          productIds.has(purchasesOrderable.id)
        ) {
          return;
        }

        const fallbackProduct = {
          ...purchasesOrderable,
          categories: purchasesOrderable.categories || item.categories || [],
          taxGroups: purchasesOrderable.taxGroups || item.taxGroups || [],
          orderUnitOfMeasureUnit:
            purchasesOrderable.orderUnitOfMeasureUnit ||
            item.orderUnitOfMeasureUnit ||
            null,
          orderUnitOfMeasureUnitPrice:
            purchasesOrderable.orderUnitOfMeasureUnitPrice ||
            item.orderUnitOfMeasureUnitPrice ||
            null,
          supplierProduct:
            purchasesOrderable.supplierProduct || item.supplierProduct || {},
          warehouseProduct:
            purchasesOrderable.warehouseProduct ||
            item.warehouseProduct ||
            null,
          replacements:
            purchasesOrderable.replacements || item.replacements || [],
          special_order:
            typeof purchasesOrderable.special_order !== "undefined"
              ? purchasesOrderable.special_order
              : false,
        };

        augmentedProducts.push(fallbackProduct);
        productIds.add(fallbackProduct.id);
      });

      const filteredData = augmentedProducts.filter((product) => {
        const matchesQuery =
          !this.query ||
          this.$unaccent(product.code || "")
            .toLowerCase()
            .includes(this.$unaccent(query).toLowerCase()) ||
          this.$unaccent(product.sku || "")
            .toLowerCase()
            .includes(this.$unaccent(query).toLowerCase()) ||
          this.$unaccent(product.name || "")
            .toLowerCase()
            .includes(this.$unaccent(query).toLowerCase()) ||
          this.$unaccent(product.supplierProduct?.code || "")
            .toLowerCase()
            .includes(this.$unaccent(query).toLowerCase()) ||
          this.$unaccent(product.supplierProduct?.sku || "")
            .toLowerCase()
            .includes(this.$unaccent(query).toLowerCase());

        const quantity = this.itemsQuantities[product.id] || 0;
        const matchesQuantityFilter = this.displayZeroQuantities
          ? true
          : quantity > 0;

        const matchCategory =
          !this.selectedCategory ||
          product.categories?.some(
            (category) => category.id === this.selectedCategory
          );

        return matchesQuery && matchesQuantityFilter && matchCategory;
      });

      this.filteredProducts = filteredData;
    },

    hasHalfUnitSurcharge(product) {
      if (product.supplierProduct) {
        if (product.supplierProduct.can_have_half_unit) {
          return true;
        }
      }
      return false;
    },

    resetProductQuantity(product) {
      const foundItem = this.purchasesOrder.items.find((item) => {
        return item.purchasesOrderable.id == product.id;
      });
      if (foundItem) {
        this.itemsQuantities[product.id] = foundItem.quantity;
      } else {
        this.itemsQuantities[product.id] = 0;
      }
    },
  },

  watch: {
    purchasesOrder: {
      handler() {
        this.getProducts();
        this.syncPurchasesOrderItems();

        // this.displayZeroQuantities =
        //   this.purchasesOrder.status == ORDER_STATUS_DRAFT;
      },
      deep: true,
    },

    query: {
      handler() {
        this.filterProducts();
      },
      immediate: true,
      deep: true,
    },

    displayZeroQuantities: {
      handler() {
        this.filterProducts();
      },
      immediate: true,
      deep: true,
    },

    pagination: {
      handler() {
        this.filterProducts();
      },
      immediate: false,
      deep: true,
    },

    selectedCategory: {
      handler() {
        this.filterProducts();
      },
      immediate: false,
      deep: true,
    },
  },
};
</script>

<style>
.purchasesOrder-item-excerpt p,
.purchasesOrder-item-excerpt,
.purchasesOrder-item-excerpt * {
  font-size: 0.7rem;
}

.special-order-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.65rem;
  text-transform: uppercase;
}

.half-unit-badge {
  display: inline-flex;
  align-items: center;
  font-size: 0.8rem;
  font-weight: bold;
  cursor: help;
  padding: 0.25em 0.5em;
}

.replacement-actions .action-icon {
  cursor: pointer;
}

.replacement-link {
  font-size: 0.85rem;
}

.replacement-meta {
  font-size: 0.9rem;
  font-weight: 600;
  color: #1f2d3d;
}

.replacement-target-highlight {
  animation: replacement-target-pulse 10s ease;
}

.replacement-target-highlight > td {
  background-color: #f8d7da !important;
  transition: background-color 0.3s ease;
}

@keyframes replacement-target-pulse {
  0% {
    background-color: #f8d7da;
  }
  80% {
    background-color: #f8d7da;
  }
  100% {
    background-color: transparent;
  }
}
</style>
